/**
 * English - `ourTeam`: the Our team screen - the people a shop shows on its
 * storefront and takes bookings with (doctors, stylists, consultants). Not the
 * staff accounts under Settings > Team members, which live in `team`.
 *
 * `{singular}` and `{plural}` are the shop's own words for its people
 * ("Doctor" / "Doctors"), set in the "What we call them" block.
 */
const ourTeam: Record<string, string> = {
  title: 'Our team',
  person: 'Person',
  intro:
    'The people shoppers meet. Visible {plural} appear in the Team section of your storefront and in your booking form, in this order. These are not dashboard logins - those are under Team members.',
  add: 'Add {singular}',
  loading: 'Loading…',
  load_error: 'This list could not be loaded. Please refresh the page.',
  empty_title: 'Nobody added yet',
  empty_description:
    'Add the first {singular} shoppers can read about and book with - a photo, a role and a short bio is enough to start.',
  read_only: 'You can view this list but not change it.',
  visible: 'Shown',
  hidden: 'Hidden',
  all_services: 'Offers every service',
  some_services: 'Offers: {services}',
  own_hours: 'Own working hours',
  section_hours: "Booking section's hours",
  experience_new: 'Less than a year of experience',
  experience_year: '1 year experience',
  experience_years: '{years} years experience',
  speaks: 'Speaks {languages}',
  move_up: 'Move up',
  move_down: 'Move down',
  edit: 'Edit',
  delete: 'Delete',
  delete_title: 'Delete {singular}',
  delete_confirm:
    'Delete {name}? They disappear from your storefront and booking form. Past appointments keep their name. This cannot be undone.',
  saved: 'Saved.',
  deleted: 'Deleted.',
  generic_error: 'That did not save. Please try again.',

  'words.title': 'What we call them',
  'words.intro':
    'The word your storefront and this screen use for your people. Your industry suggests "{default_singular}" / "{default_plural}".',
  'words.singular': 'One person',
  'words.plural': 'More than one',
  'words.save': 'Save words',
  'words.reset': 'Use the industry default',
  'words.saved': 'Words saved.',

  'form.add_title': 'Add {singular}',
  'form.edit_title': 'Edit {singular}',
  'form.details': 'About them',
  'form.name': 'Name',
  'form.photo': 'Photo',
  'form.photo_hint': 'A clear, friendly head-and-shoulders photo works best.',
  'form.photo_frame_portrait': 'Portrait card',
  'form.photo_frame_round': 'Round photo',
  'form.role': 'Role',
  'form.role_placeholder': 'Senior stylist',
  'form.speciality': 'Speciality',
  'form.speciality_placeholder': 'Hair colour',
  'form.qualifications': 'Qualifications',
  'form.qualifications_placeholder': 'MBBS, MD',
  'form.short_bio': 'Short bio',
  'form.bio': 'Full bio',
  'form.fee_note': 'Fee note',
  'form.fee_note_placeholder': 'Consultation from ₹800',
  'form.is_visible': 'Show on the storefront',
  'form.login': 'Login account',
  'form.login_none': 'No login',
  'form.login_taken': '{login} - already {name}',
  'form.login_hint': 'Lets this {singular} sign in and see only their own appointments. On Team members, give the login "My appointments only" and, to set their own hours, "My schedule".',
  signs_in: 'Signs in',
  signs_in_as: 'Signs in as {name}',
  'form.services': 'Services they offer',
  'form.services_hint': 'Tick none and they offer every service.',
  'form.services_empty':
    'Your theme has no bookings section with services yet. Until it does, everyone offers every service.',
  'form.services_page': 'on {page}',
  'form.hours': 'Working hours',
  'form.hours_follow': "Follow the booking section's hours",
  'form.hours_follow_hint': 'They can be booked whenever your booking section takes appointments.',
  'form.hours_custom': 'Set their own hours',
  'form.hours_custom_hint': 'For someone who works fewer days or different times.',

  'form.profile': 'Profile details',
  'form.profile_hint':
    'All optional. Shoppers can filter your Team section by these and read them on the profile page.',
  'form.gender': 'Gender',
  'form.gender.unset': 'Prefer not to say',
  'form.gender.female': 'Female',
  'form.gender.male': 'Male',
  'form.gender.other': 'Other',
  'form.experience_years': 'Years of experience',
  'form.experience_years_placeholder': '12',
  'form.visit_formats': 'How shoppers can see them',
  'form.visit_format.in_person': 'In person',
  'form.visit_format.online': 'Online',
  'form.visit_format.home': 'Home visit',
  'form.languages': 'Languages they speak',
  'form.languages_placeholder': 'Hindi, English',
  'form.languages_hint': 'Press Enter or a comma after each. Up to {max}.',
  'form.languages_full': 'That is the most - {max} languages. Remove one to add another.',
  'form.language_remove': 'Remove {language}',

  'form.education': 'Education',
  'form.education_empty': 'Degrees, diplomas and training, newest or oldest first - the order you set is the order shown.',
  'form.education_add': 'Add education',
  'form.education_entry': 'Education {n}',
  'form.education_year': 'Year',
  'form.education_year_placeholder': '2012',
  'form.education_title': 'Degree or course',
  'form.education_title_placeholder': 'MD, Dermatology',
  'form.education_place': 'Where',
  'form.education_place_placeholder': 'AIIMS, New Delhi',

  'form.career': 'Career',
  'form.career_empty': 'Where they have worked before and what they did there.',
  'form.career_add': 'Add a role',
  'form.career_entry': 'Career {n}',
  'form.career_period': 'When',
  'form.career_period_placeholder': '2015 - 2020',
  'form.career_title': 'Role',
  'form.career_title_placeholder': 'Consultant dermatologist',
  'form.career_place': 'Where',
  'form.career_place_placeholder': 'Apollo Hospital, Chennai',

  'form.entry_remove': 'Remove',
  'form.entries_full': 'Up to {max} entries.',

  'form.save': 'Save',
  'form.saving': 'Saving…',
  'form.cancel': 'Cancel',

  'errors.required': 'This field is required.',
  'errors.hours': 'On {day}, the finishing time must be after the starting time.',
  'errors.experience': 'Enter whole years from 0 to {max}, or leave it empty.',
  'errors.entry_title': 'Add what this was, or remove the entry.',
  'errors.all_closed': 'Open at least one day, or follow the booking section\'s hours.',
};

export default ourTeam;
